import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/format';

// Per document: bump the date whenever its text changes.
const LAST_UPDATED = { privacy: new Date(2026, 9, 19) };
const DEFAULT_UPDATED = new Date(2026, 4, 10);

const sectionsOf = (t, ns) => {
  const list = t(`legal.${ns}.sections`, { returnObjects: true });
  return Array.isArray(list) ? list : [];
};

/**
 * Privacy / Terms page body. The text comes from the `legal` locale fragment.
 * Turkish is the authoritative version (Turkish law, KVKK): other languages show
 * a translation notice and can reveal the Turkish original.
 */
const LegalDocument = ({ ns }) => {
  const { t, i18n } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  const isTurkish = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0] === 'tr';
  const original = i18n.getFixedT('tr');
  const useOriginal = !isTurkish && showOriginal;
  const tx = useOriginal ? original : t;

  // The page renders after the route loads, so the browser can't jump to
  // /privacy#s11 by itself.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    const el = id && document.getElementById(id);
    if (el && el.scrollIntoView) el.scrollIntoView();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-8">
        <Link to="/signup" className="text-sm text-blue-600 hover:underline">
          <span className="inline-block rtl:rotate-180">←</span> {t('legal.common.back')}
        </Link>
        {!isTurkish && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex flex-wrap items-center gap-2">
            <span>{t('legal.common.translationNotice')}</span>
            <button
              type="button"
              className="font-medium text-blue-700 hover:underline"
              onClick={() => setShowOriginal((v) => !v)}
            >
              {showOriginal ? t('legal.common.showTranslation') : t('legal.common.showOriginal')}
            </button>
          </div>
        )}
        <div {...(useOriginal ? { lang: 'tr', dir: 'ltr', 'data-i18n-ignore': '' } : {})}>
          <h1 className="text-3xl font-bold mt-4 mb-2">{tx(`legal.${ns}.title`)}</h1>
          <p className="text-sm text-gray-500 mb-6">
            {t('legal.common.lastUpdated', { date: formatDate(LAST_UPDATED[ns] || DEFAULT_UPDATED) })}
          </p>
          <p className="text-gray-700 mb-6">{tx(`legal.${ns}.intro`)}</p>
          {sectionsOf(tx, ns).map((s, i) => (
            // #s<n> links to a section (Google Play: /privacy#s11 = account deletion)
            <section key={s.h} id={`s${i + 1}`} className="mb-5 scroll-mt-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">{s.h}</h2>
              <p className="text-gray-700 leading-relaxed">{s.p}</p>
            </section>
          ))}
          <p className="text-sm text-gray-500 mt-8 pt-6 border-t">{tx(`legal.${ns}.contact`)}</p>
        </div>
      </div>
    </div>
  );
};

export default LegalDocument;
